/**
 * Attendance.jsx — Orchestrator
 *
 * Owns:
 *   - All data-fetching (users + daily/monthly attendance)
 *   - Caching layer (30-second TTL per key)
 *   - Check-in / check-out confirm flow
 *   - Permission helpers
 *   - CSS injection
 *   - Tab switching
 *
 * Renders DailyAttendance / MonthlyAttendanceModern as pure view components.
 * Shared stat tiles + search / department / chip filters live in AttendanceSharedUI
 * (AttendanceStatGrid, AttendancePanelFilters, AttendanceSearchDepartmentRow, AttendanceFilterChipRow).
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box, Tabs, Tab, Button, Alert, Container, Typography,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Card, CardContent, alpha,
} from '@mui/material';
import {
  AccessTime as AccessTimeIcon,
  CheckCircle as CheckCircleIcon,
  CalendarMonth as CalendarMonthIcon,
  LocationOn as LocationOnIcon,
  Groups as GroupsIcon,
  HowToReg as HowToRegIcon,
  PersonOff as PersonOffIcon,
  Percent as PercentIcon,
} from '@mui/icons-material';

import Header   from '../../common/Header';
import Loading  from '../../common/Loading';
import FeedbackMessage from '../../common/FeedbackMessage';
import StatCards from '../../common/StatCards';
import FilterBar from '../../common/FilterBar';
import ConfirmDeleteDialog from '../../common/ConfirmDeleteDialog';

import axios    from '../../../utils/axios';
import { useAuth }     from '../../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { motion }      from 'framer-motion';
import { getNavIconForPath } from '../../../utils/navIcons';
import { formatMalaysiaTime, toMalaysiaLocalDateString } from '../../../utils/malaysiaTime';
import { canUpdateAttendance, canUpdateAttendanceStatus } from '../../../utils/attendancePermissions';

import DailyAttendance      from './DailyAttendance';
import MonthlyAttendance    from './MonthlyAttendanceModern';

import {
  C, R, FONT_UI, TRANSITION, ATT_CSS,
  glassCardSx, getDepartments, isUserPresent, getStatusMeta,
  normalizeAttendanceDate, sameAttendanceRecord,
} from './AttendanceSharedUI';
import FaceAttendanceLocationDialog from './FaceAttendanceLocationDialog';

const MotionBox = motion(Box);
const NEON3 = '#34D399';

// ─── Attendance ───────────────────────────────────────────────────────────────
const Attendance = () => {
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();

  // ── View state ──
  const [tab,          setTab]          = useState(0);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState(null);
  const [alert,        setAlert]        = useState({ type: '', message: '' });
  const [searchTerm,   setSearchTerm]   = useState('');
  const [showFilters,  setShowFilters]  = useState(false);
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // ── Data state ──
  const [users,          setUsers]          = useState([]);
  const [attendanceData, setAttendanceData] = useState([]);
  const [yearlyAttendanceData, setYearlyAttendanceData] = useState([]);
  const [currentDate,    setCurrentDate]    = useState(new Date());
  const [currentMonth,   setCurrentMonth]   = useState(new Date());

  // ── Cache ──
  const [attCache,     setAttCache]     = useState({});
  const [usersCache,   setUsersCache]   = useState(null);
  const [lastFetch,    setLastFetch]    = useState({});

  // ── Confirm dialog ──
  const [confirm, setConfirm] = useState({ open: false, type: '', userId: null });
  const [reasonDialog, setReasonDialog] = useState({ open: false, userId: null, date: '', status: '' });
  const [absenceReason, setAbsenceReason] = useState('');
  const [faceLocationDialogOpen, setFaceLocationDialogOpen] = useState(false);

  // ── CSS injection ──
  useEffect(() => {
    const id = 'att-global-css';
    if (!document.getElementById(id)) {
      const el = document.createElement('style');
      el.id = id; el.innerHTML = ATT_CSS;
      document.head.appendChild(el);
    }
  }, []);

  // ── Derived date strings ──
  const ds = useMemo(() => ({
    today:   toMalaysiaLocalDateString(new Date()),
    current: toMalaysiaLocalDateString(currentDate),
    month:   `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, '0')}`,
    year:    String(currentMonth.getFullYear()),
  }), [currentDate, currentMonth]);

  // ── Permission helpers ──
  const permissions = useMemo(() => ({
    canUpdateAttendance: () => canUpdateAttendance(currentUser),
    canUpdateStatus: () => canUpdateAttendanceStatus(currentUser),
  }), [currentUser]);
  const departments = useMemo(() => getDepartments(users), [users]);
  const dailyRecordIndex = useMemo(() => {
    const idx = new Map();
    attendanceData.forEach((r) => idx.set(r.user_id, r));
    return idx;
  }, [attendanceData]);
  const daysInCurrentMonth = useMemo(
    () => new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0).getDate(),
    [currentMonth],
  );
  const currentYearMonth = useMemo(
    () => `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, '0')}`,
    [currentMonth],
  );
  const monthlyCounts = useMemo(() => {
    if (tab !== 1) return new Map();
    const attIndex = new Map();
    attendanceData.forEach((r) => attIndex.set(`${r.user_id}::${normalizeAttendanceDate(r.date)}`, r));
    const out = new Map();
    users.forEach((u) => {
      let present = 0;
      let approved = 0;
      let absent = 0;
      let noRecord = 0;
      for (let d = 1; d <= daysInCurrentMonth; d++) {
        const dateStr = `${currentYearMonth}-${String(d).padStart(2, '0')}`;
        const rec = attIndex.get(`${u.id}::${dateStr}`);
        const meta = getStatusMeta(rec);
        if (meta.type === 'present') present += 1;
        else if (meta.type === 'approved_absent') approved += 1;
        else if (meta.type === 'absent') absent += 1;
        else noRecord += 1;
      }
      out.set(u.id, { present, approved, absent, noRecord });
    });
    return out;
  }, [tab, users, attendanceData, daysInCurrentMonth, currentYearMonth]);
  const searchedDepartmentUsers = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    return users.filter((u) => {
      if (q && !String(u.name || '').toLowerCase().includes(q)) return false;
      if (departmentFilter) {
        const id = String(u.department?.id ?? u.department_id ?? '');
        if (id !== String(departmentFilter)) return false;
      }
      return true;
    });
  }, [users, searchTerm, departmentFilter]);
  const visibleUsers = useMemo(() => {
    if (statusFilter === 'all') return searchedDepartmentUsers;
    if (tab === 0) {
      return searchedDepartmentUsers.filter((u) => {
        const present = isUserPresent(dailyRecordIndex.get(u.id));
        return statusFilter === 'present' ? present : !present;
      });
    }
    return searchedDepartmentUsers.filter((u) => {
      const c = monthlyCounts.get(u.id);
      if (!c) return false;
      if (statusFilter === 'present') return c.present > 0;
      if (statusFilter === 'absent') return c.approved + c.absent > 0;
      if (statusFilter === 'no_record') return c.noRecord === daysInCurrentMonth;
      return true;
    });
  }, [tab, statusFilter, searchedDepartmentUsers, dailyRecordIndex, monthlyCounts, daysInCurrentMonth]);
  const topStats = useMemo(() => {
    if (tab === 0) {
      const total = searchedDepartmentUsers.length;
      const present = searchedDepartmentUsers.filter((u) => isUserPresent(dailyRecordIndex.get(u.id))).length;
      const absent = Math.max(0, total - present);
      const rate = total ? Math.round((present / total) * 1000) / 10 : 0;
      return [
        { icon: GroupsIcon, value: total, label: 'Total Staff', color: 'info' },
        { icon: HowToRegIcon, value: present, label: 'Present', color: 'success' },
        { icon: PersonOffIcon, value: absent, label: 'Absent', color: 'error' },
        { icon: PercentIcon, value: `${rate}%`, label: 'Attendance Rate', color: 'warning' },
      ];
    }
    let presentDays = 0;
    let absentDays = 0;
    searchedDepartmentUsers.forEach((u) => {
      const c = monthlyCounts.get(u.id);
      if (!c) return;
      presentDays += c.present;
      absentDays += c.approved + c.absent;
    });
    const total = Math.max(1, searchedDepartmentUsers.length * daysInCurrentMonth);
    const rate = Math.round((presentDays / total) * 10000) / 100;
    return [
      { icon: GroupsIcon, value: searchedDepartmentUsers.length, label: 'Total Staff', color: 'info' },
      { icon: HowToRegIcon, value: presentDays, label: 'Present Days', color: 'success' },
      { icon: PersonOffIcon, value: absentDays, label: 'Absent Days', color: 'error' },
      { icon: PercentIcon, value: `${rate}%`, label: 'Attendance Rate', color: 'warning' },
    ];
  }, [tab, searchedDepartmentUsers, dailyRecordIndex, monthlyCounts, daysInCurrentMonth]);
  const filterOptions = useMemo(() => {
    const base = [{
      name: 'department',
      label: 'Department',
      options: departments.map(([id, name]) => ({ value: id, label: name })),
    }];
    if (tab === 0) {
      return [...base, {
        name: 'status',
        label: 'Status',
        options: [
          { value: 'present', label: 'Present' },
          { value: 'absent', label: 'Absent' },
        ],
      }];
    }
    return [...base, {
      name: 'status',
      label: 'Status',
      options: [
        { value: 'present', label: 'Has Present' },
        { value: 'absent', label: 'Has Absent' },
        { value: 'no_record', label: 'No Record Only' },
      ],
    }];
  }, [departments, tab]);
  const activeStatIndex = statusFilter === 'present' ? 1 : statusFilter === 'absent' ? 2 : statusFilter === 'no_record' ? 3 : 0;

  useEffect(() => {
    if (tab === 0 && statusFilter === 'no_record') {
      setStatusFilter('all');
    }
  }, [tab, statusFilter]);

  // ── Fetch users (cached) ──
  const fetchUsers = useCallback(async () => {
    if (usersCache) return usersCache;
    try {
      const res       = await axios.get('/users');
      const processed = res.data.map((u) => ({ ...u, is_admin: Boolean(u.is_admin) }));
      setUsersCache(processed);
      setUsers(processed);
      return processed;
    } catch {
      setError('Failed to fetch users. Please try again.');
      return [];
    }
  }, [usersCache]);

  // ── Fetch attendance (30 s TTL cache) ──
  const fetchAttendance = useCallback(async (key, type, force = false) => {
    const cacheKey = `${type}::${key}`;
    const now      = Date.now();
    if (!force && attCache[cacheKey] && now - (lastFetch[cacheKey] || 0) < 30_000) {
      return attCache[cacheKey];
    }

    try {
      let data = [];
      if (type === 'daily') {
        const res = await axios.get('/attendance/daily', { params: { date: key } });
        data = res.data;
      } else if (type === 'monthly') {
        const [year, month] = String(key).split('-').map(Number);
        const res = await axios.get('/attendance/monthly', { params: { month, year }, cache: false });
        data = res.data.map((r) => ({ ...r, date: normalizeAttendanceDate(r.date) }));
      } else {
        const year = Number(key);
        const monthlyResponses = await Promise.all(
          Array.from({ length: 12 }, (_, i) =>
            axios.get('/attendance/monthly', {
              params: { month: i + 1, year },
              cache: false,
            }),
          ),
        );
        data = monthlyResponses
          .flatMap((res) => res.data || [])
          .map((r) => ({ ...r, date: normalizeAttendanceDate(r.date) }));
      }

      setAttCache((prev) => ({ ...prev, [cacheKey]: data }));
      setLastFetch((prev) => ({ ...prev, [cacheKey]: now }));
      return data;
    } catch {
      setError(`Failed to fetch ${type} attendance.`);
      return [];
    }
  }, [currentMonth, attCache, lastFetch]);

  // ── Invalidate cache entry ──
  const invalidate = useCallback((type, key) => {
    const cacheKey = `${type}::${key}`;
    setAttCache((prev) => {
      if (!(cacheKey in prev)) return prev;
      const next = { ...prev };
      delete next[cacheKey];
      return next;
    });
    setLastFetch((prev) => ({ ...prev, [cacheKey]: 0 }));
  }, []);

  const refreshMonthlyAttendance = useCallback(async () => {
    invalidate('monthly', ds.month);
    invalidate('yearly', ds.year);
    const [monthData, yearData] = await Promise.all([
      fetchAttendance(ds.month, 'monthly', true),
      fetchAttendance(ds.year, 'yearly', true),
    ]);
    setAttendanceData(monthData);
    setYearlyAttendanceData(yearData);
    return { monthData, yearData };
  }, [ds.month, ds.year, fetchAttendance, invalidate]);

  // ── Initial load ──
  useEffect(() => {
    if (!currentUser) { navigate('/login'); return; }
    const init = async () => {
      setLoading(true);
      try {
        const [usersData, attData] = await Promise.all([
          fetchUsers(),
          fetchAttendance(ds.current, 'daily'),
        ]);
        setUsers(usersData);
        setAttendanceData(attData);
        setYearlyAttendanceData([]);
      } catch {
        setError('Failed to load attendance data.');
      } finally {
        setLoading(false);
      }
    };
    init();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, ds.current]);

  // ── Tab change ──
  const handleTabChange = useCallback(async (_, newTab) => {
    setTab(newTab);
    if (newTab === 1) {
      const [monthData, yearData] = await Promise.all([
        fetchAttendance(ds.month, 'monthly'),
        fetchAttendance(ds.year, 'yearly'),
      ]);
      setAttendanceData(monthData);
      setYearlyAttendanceData(yearData);
      return;
    }
    const data = await fetchAttendance(ds.current, 'daily');
    setAttendanceData(data);
    setYearlyAttendanceData([]);
  }, [ds.month, ds.current, ds.year, fetchAttendance]);

  // ── Daily date navigation ──
  const handleDateChange = useCallback(async (direction) => {
    const d = new Date(currentDate);
    d.setDate(d.getDate() + direction);
    setCurrentDate(d);
    const key  = toMalaysiaLocalDateString(d);
    const data = await fetchAttendance(key, 'daily');
    setAttendanceData(data);
  }, [currentDate, fetchAttendance]);

  const handleSelectDate = useCallback(async (dateStr) => {
    if (!dateStr) return;
    const [y, m, d] = dateStr.split('-').map(Number);
    if (!y || !m || !d) return;
    const date = new Date(y, m - 1, d);
    setCurrentDate(date);
    const data = await fetchAttendance(dateStr, 'daily');
    setAttendanceData(data);
  }, [fetchAttendance]);

  // ── Monthly month navigation ──
  const handleMonthChange = useCallback(async (direction) => {
    const m = new Date(currentMonth);
    m.setMonth(m.getMonth() + direction);
    setCurrentMonth(m);
    const key  = `${m.getFullYear()}-${String(m.getMonth() + 1).padStart(2, '0')}`;
    const yearKey = String(m.getFullYear());
    const [data, yearData] = await Promise.all([
      fetchAttendance(key, 'monthly'),
      fetchAttendance(yearKey, 'yearly'),
    ]);
    setAttendanceData(data);
    setYearlyAttendanceData(yearData);
  }, [currentMonth, fetchAttendance]);

  const handleSelectMonth = useCallback(async (monthStr) => {
    if (!monthStr) return;
    const [y, m] = monthStr.split('-').map(Number);
    if (!y || !m) return;
    const monthDate = new Date(y, m - 1, 1);
    setCurrentMonth(monthDate);
    const key = `${monthDate.getFullYear()}-${String(monthDate.getMonth() + 1).padStart(2, '0')}`;
    const yearKey = String(monthDate.getFullYear());
    const [data, yearData] = await Promise.all([
      fetchAttendance(key, 'monthly'),
      fetchAttendance(yearKey, 'yearly'),
    ]);
    setAttendanceData(data);
    setYearlyAttendanceData(yearData);
  }, [fetchAttendance]);

  // ── Optimistic state helpers ──
  const updateRecord = (userId, date, changes) =>
    setAttendanceData((prev) => {
      let matched = false;
      const next = prev.map((r) => {
        if (r.user_id !== userId) return r;
        const sameDate = r.date ? r.date === date : true;
        if (!sameDate || matched) return r;
        matched = true;
        return { ...r, ...changes, date: r.date || date };
      });
      return next;
    });

  const upsertRecord = (userId, date, changes) => {
    const normalizedDate = normalizeAttendanceDate(date);
    setAttendanceData((prev) => {
      const exactExists = prev.some((r) => sameAttendanceRecord(r, userId, normalizedDate));
      if (exactExists) {
        return prev.map((r) => (
          sameAttendanceRecord(r, userId, normalizedDate)
            ? { ...r, ...changes, date: normalizedDate }
            : r
        ));
      }
      const dailyShapeIndex = prev.findIndex((r) => Number(r.user_id) === Number(userId) && !r.date);
      if (dailyShapeIndex !== -1) {
        const next = [...prev];
        next[dailyShapeIndex] = { ...next[dailyShapeIndex], ...changes, date: normalizedDate };
        return next;
      }
      return [...prev, { user_id: userId, date: normalizedDate, ...changes }];
    });
  };

  const upsertYearlyRecord = (userId, date, changes) => {
    const normalizedDate = normalizeAttendanceDate(date);
    setYearlyAttendanceData((prev) => {
      const exactExists = prev.some((r) => sameAttendanceRecord(r, userId, normalizedDate));
      if (exactExists) {
        return prev.map((r) => (
          sameAttendanceRecord(r, userId, normalizedDate)
            ? { ...r, ...changes, date: normalizedDate }
            : r
        ));
      }
      return [...prev, { user_id: userId, date: normalizedDate, ...changes }];
    });
  };

  const applyStatusChange = useCallback(async (userId, date, status, reason = '') => {
    const nextReason = ['approved_absent', 'absent'].includes(status)
      ? String(reason || '').trim()
      : null;

    if (['approved_absent', 'absent'].includes(status) && !nextReason) {
      setAlert({ type: 'error', message: 'Please provide a reason for absent or approved absent.' });
      return;
    }

    upsertRecord(userId, date, { status, absence_reason: nextReason });
    if (tab === 1) {
      upsertYearlyRecord(userId, date, { status, absence_reason: nextReason });
    }
    setAlert({ type: 'success', message: `Status updated to "${status.replace('_', ' ')}"` });

    try {
      const res = await axios.post('/attendance/update', {
        userId,
        date,
        status,
        absence_reason: nextReason,
      });
      if (res.data?.user_id) {
        const saved = {
          ...res.data,
          date: normalizeAttendanceDate(res.data.date),
        };
        upsertRecord(userId, date, saved);
        if (tab === 1) {
          upsertYearlyRecord(userId, date, saved);
        }
      }
      if (tab === 1) {
        await refreshMonthlyAttendance();
      } else {
        invalidate('daily', ds.current);
        invalidate('monthly', ds.month);
        invalidate('yearly', ds.year);
      }
    } catch {
      setAlert({ type: 'error', message: 'Failed to update status. Please try again.' });
      if (tab === 1) {
        await refreshMonthlyAttendance();
      }
    }
  }, [tab, refreshMonthlyAttendance, invalidate, ds.current, ds.month, ds.year]);

  // ── Check-in / check-out triggers ──
  const handleCheckIn  = (userId) => setConfirm({ open: true, type: 'checkin',  userId });
  const handleCheckOut = (userId) => setConfirm({ open: true, type: 'checkout', userId });

  const confirmAction = async () => {
    const { type, userId } = confirm;
    setConfirm({ open: false, type: '', userId: null });

    const nowStr = formatMalaysiaTime(new Date());

    if (type === 'checkin') {
      if (!permissions.canUpdateAttendance()) {
        setAlert({ type: 'error', message: 'You do not have permission to check in for others.' });
        return;
      }
      upsertRecord(userId, ds.current, { check_in_time: nowStr, status: 'present' });
      setAlert({ type: 'success', message: 'Checked in successfully!' });
      try {
        const res = await axios.post('/attendance/check-in', { userId, date: ds.current });
        if (res.data?.check_in_time) {
          upsertRecord(userId, ds.current, {
            check_in_time: res.data.check_in_time,
            status: res.data.status || 'present',
          });
        }
        if (tab === 1) {
          await refreshMonthlyAttendance();
        }
      } catch {
        setAlert({ type: 'error', message: 'Check-in failed. Please try again.' });
        if (tab === 1) await refreshMonthlyAttendance();
      } finally {
        invalidate('daily',   ds.current);
        invalidate('monthly', ds.month);
        invalidate('yearly',  ds.year);
      }
    } else {
      if (!permissions.canUpdateAttendance()) {
        setAlert({ type: 'error', message: 'You do not have permission to check out for others.' });
        return;
      }
      upsertRecord(userId, ds.current, { check_out_time: nowStr });
      setAlert({ type: 'success', message: 'Checked out successfully!' });
      try {
        const res = await axios.post('/attendance/check-out', { userId, date: ds.current });
        if (res.data?.check_out_time)
          upsertRecord(userId, ds.current, { check_out_time: res.data.check_out_time });
        if (tab === 1) {
          await refreshMonthlyAttendance();
        }
      } catch {
        setAlert({ type: 'error', message: 'Check-out failed. Please try again.' });
        if (tab === 1) await refreshMonthlyAttendance();
      } finally {
        invalidate('daily',   ds.current);
        invalidate('monthly', ds.month);
        invalidate('yearly',  ds.year);
      }
    }
  };

  // ── Status cycle (monthly grid click) ──
  const handleAttendanceClick = useCallback(async (userId, date) => {
    if (!permissions.canUpdateStatus()) {
      setAlert({ type: 'error', message: 'Only HR and team leaders can update attendance status.' });
      return;
    }
    const cur      = attendanceData.find((r) => sameAttendanceRecord(r, userId, date));
    const next     = !cur?.status
      ? 'present'
      : cur.status === 'present'
      ? 'approved_absent'
      : cur.status === 'approved_absent'
      ? 'absent'
      : 'present';

    if (next === 'approved_absent' || next === 'absent') {
      setAbsenceReason(cur?.absence_reason || '');
      setReasonDialog({ open: true, userId, date, status: next });
      return;
    }

    await applyStatusChange(userId, date, next, '');
  }, [attendanceData, permissions, applyStatusChange]);

  // ── Guards ──
  if (!currentUser) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error" sx={{ borderRadius: R.lg, fontFamily: FONT_UI }}>
          Please log in to view this page.
        </Alert>
      </Container>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Loading message="Loading attendance data" size={52} variant="primary" />
      </Box>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────────

  return (
    <Container maxWidth="lg" sx={{ mt: 2, mb: 6 }}>

      {/* ── Page header ── */}
      <Header
        title="Attendance Management"
        subtitle="Track and manage team attendance records"
        icon={getNavIconForPath('/attendance')}
        actionButton={
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {permissions.canUpdateStatus() && (
              <Button
                variant="outlined"
                startIcon={<LocationOnIcon sx={{ fontSize: 16 }} />}
                onClick={() => setFaceLocationDialogOpen(true)}
              >
                Face check-in location
              </Button>
            )}
            <Button
              variant="contained"
              startIcon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
              onClick={() => navigate('/attendance/face-scan')}
              sx={{
                fontFamily: FONT_UI, fontWeight: 700, fontSize: '0.8rem',
                borderRadius: R.lg, px: 2.5, py: 1,
                bgcolor: alpha('#fff', 0.12), color: '#fff',
                backdropFilter: 'blur(8px)', border: `1px solid ${alpha('#fff', 0.22)}`,
                textTransform: 'none',
                transition: TRANSITION,
                '&:hover': { bgcolor: alpha('#fff', 0.2) },
              }}
            >
              Face Check-In / Out
            </Button>
          </Box>
        }
      />

      {/* ── Feedback messages ── */}
      <FeedbackMessage
        message={error}
        severity="error"
        onClose={() => setError(null)}
        sx={{
          fontFamily: "'DM Sans', sans-serif", fontSize: '0.9rem', fontWeight: 500,
          background: alpha('#450a0a', 0.92),
          border: '1px solid rgba(248,113,113,0.3)',
          boxShadow: '0 4px 20px rgba(239,68,68,0.12)',
          color: '#fca5a5',
          mb: 3,
        }}
      />
      <FeedbackMessage
        message={alert.message}
        severity={alert.type || 'info'}
        onClose={() => setAlert({ type: '', message: '' })}
        sx={alert.type === 'success' ? {
          fontFamily: "'DM Sans', sans-serif", fontSize: '0.9rem', fontWeight: 500,
          background: `linear-gradient(135deg, ${alpha(NEON3, 0.1)}, ${alpha(C.bg1, 0.95)})`,
          border: `1px solid ${alpha(NEON3, 0.35)}`,
          boxShadow: `0 4px 20px ${alpha(NEON3, 0.15)}`,
          color: alpha(NEON3, 0.95),
          mb: 2,
        } : undefined}
      />

      <StatCards
        stats={topStats}
        activeIndex={activeStatIndex}
        onCardClick={(_, index) => {
          const next = index === 1 ? 'present' : index === 2 ? 'absent' : index === 3 && tab === 1 ? 'no_record' : 'all';
          setStatusFilter((prev) => (prev === next ? 'all' : next));
        }}
      />

      <MotionBox
        component={Card}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.22, delay: 0.08 }}
        elevation={0}
        sx={{ ...glassCardSx, mb: 2.5 }}
      >
        <CardContent sx={{ p: 2.5, position: 'relative', zIndex: 1 }}>
          <FilterBar
            key={tab === 0 ? 'daily' : 'monthly'}
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            filters={{ department: departmentFilter, status: statusFilter === 'all' ? '' : statusFilter }}
            onFilterChange={(next) => {
              setDepartmentFilter(next.department || '');
              setStatusFilter(next.status || 'all');
            }}
            onClearFilters={() => {
              setSearchTerm('');
              setDepartmentFilter('');
              setStatusFilter('all');
            }}
            filterOptions={filterOptions}
            showFilters={showFilters}
            onToggleFilters={() => setShowFilters((prev) => !prev)}
            searchPlaceholder={
              tab === 0
                ? 'Search daily roster by name or department...'
                : 'Search monthly roster by member name...'
            }
          />
        </CardContent>
      </MotionBox>

      {/* ── Tab bar ── */}
      <MotionBox
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.22, delay: 0.1 }}
        sx={{ ...glassCardSx, mb: 2.5 }}
      >
        <Tabs
          value={tab}
          onChange={handleTabChange}
          sx={{
            px: 2,
            '& .MuiTabs-indicator': {
              background: C.blue, height: 2.5, borderRadius: '2px 2px 0 0',
              boxShadow: `0 0 8px ${alpha(C.blue, 0.5)}`,
            },
            '& .MuiTab-root': {
              fontFamily: FONT_UI, fontWeight: 600, fontSize: '0.83rem',
              textTransform: 'none', color: C.textSecondary, minWidth: 130,
              transition: TRANSITION, py: 1.75,
              '&.Mui-selected': { color: C.textPrimary },
            },
          }}
        >
          <Tab icon={<AccessTimeIcon sx={{ fontSize: 16 }} />}    label="Daily View"   iconPosition="start" />
          <Tab icon={<CalendarMonthIcon sx={{ fontSize: 16 }} />} label="Monthly View" iconPosition="start" />
        </Tabs>
      </MotionBox>

      {/* ── Tab panels ── */}
      {tab === 0 && (
        <DailyAttendance
          users={visibleUsers}
          attendanceData={attendanceData}
          currentDate={currentDate}
          dateStrings={ds}
          permissions={permissions}
          onDateChange={handleDateChange}
          onSelectDate={handleSelectDate}
          onCheckIn={handleCheckIn}
          onCheckOut={handleCheckOut}
          onAttendanceClick={handleAttendanceClick}
        />
      )}

      {tab === 1 && (
        <MonthlyAttendance
          users={visibleUsers}
          attendanceData={attendanceData}
          yearlyAttendanceData={yearlyAttendanceData}
          currentMonth={currentMonth}
          dateStrings={ds}
          permissions={permissions}
          onMonthChange={handleMonthChange}
          onSelectMonth={handleSelectMonth}
          onAttendanceClick={handleAttendanceClick}
          statusFilter={statusFilter}
        />
      )}

      <FaceAttendanceLocationDialog
        open={faceLocationDialogOpen}
        onClose={() => setFaceLocationDialogOpen(false)}
        onSaved={(msg) => setAlert({ type: 'success', message: msg })}
        onError={(msg) => setAlert({ type: 'error', message: msg })}
      />

      <ConfirmDeleteDialog
        open={confirm.open}
        onClose={() => setConfirm({ open: false, type: '', userId: null })}
        onConfirm={confirmAction}
        title={confirm.type === 'checkin' ? 'Confirm Check In' : 'Confirm Check Out'}
        itemType={confirm.type === 'checkin' ? 'attendance check-in' : 'attendance check-out'}
        itemName=""
        actionVerb="record"
        description={confirm.type === 'checkin'
          ? 'This will record a check-in at the current time. Continue?'
          : 'This will record a check-out at the current time. Continue?'}
        confirmLabel="Confirm"
        cancelLabel="Cancel"
        variant="edit"
        accentColor={C.blue}
        icon={<CheckCircleIcon />}
      />

      <Dialog
        open={reasonDialog.open}
        onClose={() => {
          setReasonDialog({ open: false, userId: null, date: '', status: '' });
          setAbsenceReason('');
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: R.lg,
            background: `linear-gradient(160deg, ${alpha(C.bg1, 0.98)} 0%, ${alpha(C.bg0, 0.99)} 100%)`,
            border: `1px solid ${C.border}`,
          },
        }}
      >
        <DialogTitle sx={{ color: C.textPrimary, fontFamily: FONT_UI, fontWeight: 700 }}>
          Add Absence Reason
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: C.textSecondary, fontFamily: FONT_UI, fontSize: 13, mb: 1.5 }}>
            This reason will be stored for attendance recording purposes.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={3}
            value={absenceReason}
            onChange={(e) => setAbsenceReason(e.target.value)}
            placeholder="Enter reason for absent / approved absent"
            sx={{
              '& .MuiOutlinedInput-root': {
                color: C.textPrimary,
                bgcolor: alpha(C.bg2, 0.5),
                '& fieldset': { borderColor: C.border },
                '&:hover fieldset': { borderColor: C.border2 },
                '&.Mui-focused fieldset': { borderColor: alpha(C.blue, 0.5) },
              },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={() => {
              setReasonDialog({ open: false, userId: null, date: '', status: '' });
              setAbsenceReason('');
            }}
            sx={{ color: C.textSecondary, textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            onClick={async () => {
              const { userId, date, status } = reasonDialog;
              const reason = absenceReason.trim();
              if (!reason) {
                setAlert({ type: 'error', message: 'Reason is required for absent and approved absent.' });
                return;
              }
              setReasonDialog({ open: false, userId: null, date: '', status: '' });
              setAbsenceReason('');
              await applyStatusChange(userId, date, status, reason);
            }}
            variant="contained"
            sx={{ textTransform: 'none' }}
          >
            Save Reason
          </Button>
        </DialogActions>
      </Dialog>

    </Container>
  );
};

export default React.memo(Attendance);
